/**
 * Dependency-free SVG donut chart. Purely presentational.
 */
interface Segment {
    label: string;
    value: number;
    color: string;
}

interface DonutChartProps {
    segments: Segment[];
    size?: number;
    thickness?: number;
    centerLabel?: string;
    centerSub?: string;
}

export default function DonutChart({
    segments,
    size = 168,
    thickness = 20,
    centerLabel,
    centerSub,
}: DonutChartProps) {
    const total = segments.reduce((s, seg) => s + (seg.value || 0), 0);
    const radius = (size - thickness) / 2;
    const circumference = 2 * Math.PI * radius;
    const cx = size / 2;
    const cy = size / 2;

    let offset = 0;

    return (
        <div className="tw-flex tw-items-center tw-gap-5">
            <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
                <circle cx={cx} cy={cy} r={radius} fill="none" stroke="#EEF2F7" strokeWidth={thickness} />
                {total > 0 &&
                    segments.map((seg, i) => {
                        const frac = (seg.value || 0) / total;
                        const dash = frac * circumference;
                        const el = (
                            <circle
                                key={i}
                                cx={cx}
                                cy={cy}
                                r={radius}
                                fill="none"
                                stroke={seg.color}
                                strokeWidth={thickness}
                                strokeDasharray={`${dash} ${circumference - dash}`}
                                strokeDashoffset={-offset}
                                strokeLinecap="round"
                                transform={`rotate(-90 ${cx} ${cy})`}
                            />
                        );
                        offset += dash;
                        return el;
                    })}
                {centerLabel !== undefined && (
                    <text x={cx} y={cy - 2} textAnchor="middle" fontSize="24" fontWeight="700" fill="#0F172A">
                        {centerLabel}
                    </text>
                )}
                {centerSub && (
                    <text x={cx} y={cy + 18} textAnchor="middle" fontSize="11" fill="#94A3B8">
                        {centerSub}
                    </text>
                )}
            </svg>
            <div className="tw-flex tw-flex-col tw-gap-2.5">
                {segments.map((seg, i) => (
                    <div key={i} className="tw-flex tw-items-center tw-gap-2">
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: seg.color }} />
                        <span style={{ fontSize: 13, color: "#475569", fontWeight: 500 }}>{seg.label}</span>
                        <span style={{ fontSize: 13, color: "#0F172A", fontWeight: 700, marginLeft: 4 }}>
                            {seg.value}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
}
